import { z } from 'zod'

const STORAGE_KEY = 'robojastip:profile-onboarding:v1'
const markersSchema = z.record(z.string(), z.enum(['new-registration', 'wa-existing']))
type MarkerStorage = Pick<Storage, 'getItem' | 'setItem'>
type Markers = z.infer<typeof markersSchema>

function browserStorage(): MarkerStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function readMarkers(storage: MarkerStorage | null): Markers {
  try {
    const parsed = markersSchema.safeParse(
      JSON.parse(storage?.getItem(STORAGE_KEY) ?? '{}') as unknown,
    )
    return parsed.success ? parsed.data : {}
  } catch {
    return {}
  }
}

function saveMarkers(markers: Markers, storage: MarkerStorage | null) {
  if (!storage) return false
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(markers))
    return true
  } catch {
    return false
  }
}

function phoneKey(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return `phone:${digits.startsWith('0') ? `62${digits.slice(1)}` : digits.startsWith('8') ? `62${digits}` : digits}`
}

/** Records actual registration success, not a first visit in this browser. */
export function markNewProfileRegistration(phone: string, storage = browserStorage()) {
  if (!phone.replace(/\D/g, '')) return false
  return saveMarkers({ ...readMarkers(storage), [phoneKey(phone)]: 'new-registration' }, storage)
}

/** Login may occur later if the automatic login following registration failed. */
export function claimNewProfileRegistration(
  accountId: string,
  phone: string | null | undefined,
  storage = browserStorage(),
) {
  if (!accountId || !phone) return false
  const markers = readMarkers(storage)
  const pendingKey = phoneKey(phone)
  if (markers[pendingKey] !== 'new-registration') return false
  const accountKey = `account:${accountId}`
  const next = {
    ...markers,
    [accountKey]:
      markers[accountKey] === 'wa-existing'
        ? ('wa-existing' as const)
        : ('new-registration' as const),
  }
  delete next[pendingKey]
  return saveMarkers(next, storage)
}

export function needsNewAccountProfile(accountId: string, storage = browserStorage()) {
  return readMarkers(storage)[`account:${accountId}`] === 'new-registration'
}

export function rememberExistingWaAccount(accountId: string, storage = browserStorage()) {
  if (!accountId) return false
  return saveMarkers({ ...readMarkers(storage), [`account:${accountId}`]: 'wa-existing' }, storage)
}

export function shouldShowProfileOnboarding({
  isNewAccount,
  isComplete,
  groupsConfirmed,
  hasConnectedGroup,
}: {
  isNewAccount: boolean
  isComplete: boolean
  groupsConfirmed: boolean
  hasConnectedGroup: boolean
}) {
  return isNewAccount && !isComplete && groupsConfirmed && !hasConnectedGroup
}
